import { useState, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { api } from '@/App';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_volley-club/artifacts/dg49xs0l_Black%20Decorative%20Shield%20Volleyball%20Club%20Logo%20%28Sweatshirts%29%20%28T-Shirts%29.png";

export default function LoginPage({ onLogin }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Where to land after login: an internal ?next= path (e.g. admin email deep-link), else /home
  const nextParam = new URLSearchParams(location.search).get('next');
  const decodedNext = nextParam ? decodeURIComponent(nextParam) : null;
  const safeNext = decodedNext && decodedNext.startsWith('/') && !decodedNext.startsWith('//') ? decodedNext : null;
  const [loading, setLoading] = useState(false);
  const submittingRef = useRef(false); // hard guard against double-submit (mobile double-tap race)
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Hard guard against double-submission. `disabled={loading}` has a render-gap race:
    // a fast double-tap (common on mobile when a cold backend stalls the first request)
    // can fire handleSubmit twice before the disabled state commits — one request then
    // succeeds ("Welcome back!" + token) while the other fails ("Server is waking up"),
    // producing the confusing dual-toast some users reported.
    if (submittingRef.current) return;
    submittingRef.current = true;
    setLoading(true);

    // Step 1: the network request. This is the ONLY thing that may surface an error toast.
    let response;
    try {
      response = await api.post('/auth/login', formData);
    } catch (error) {
      // If another (concurrent) attempt already logged us in, never show a failure — just go.
      if (localStorage.getItem('token')) {
        window.location.href = safeNext || '/home';
      } else {
        // Distinguish bad credentials (401/403) from transient network/server issues
        // so a cold-start hiccup is not reported as a hard "login failed".
        const status = error.response?.status;
        if (status === 401 || status === 403) {
          toast.error(error.response?.data?.detail || 'Invalid email or password');
        } else if (!error.response || status >= 500) {
          toast.error('Server is waking up. Please tap "Sign In" again in a moment.');
        } else {
          toast.error(error.response?.data?.detail || 'Login failed');
        }
      }
      submittingRef.current = false;
      setLoading(false);
      return;
    }

    // Step 2: login SUCCEEDED — the token is valid. From here on we must NEVER show a
    // failure toast, even if navigation/state updates throw (that was the production bug:
    // a post-success error fell into the catch and showed "Server is waking up" while the
    // user was actually logged in).
    try {
      const data = response.data || {};
      const token = data.access_token;
      const userData = data.user;

      if (token && userData) {
        onLogin(token, userData);
        toast.success('Welcome back!');
        // Redirect is handled declaratively by <LoginRoute> (honors ?next=) to avoid
        // a navigate()-vs-setState race that could bounce admins to /home.
      } else if (token) {
        // Unexpected: 200 with a token but no user body. Store the token safely and
        // hard-reload so the app rehydrates the user from /auth/me. (No 'user' write —
        // that could exceed the localStorage quota for users with large base64 photos.)
        try { localStorage.setItem('token', token); } catch (e) { console.error('token persist failed:', e); }
        toast.success('Welcome back!');
        window.location.href = safeNext || '/home';
      } else {
        toast.error('Login failed. Please try again.');
      }
    } catch (postErr) {
      // Login already succeeded (token stored). Swallow render/navigation errors so the
      // user never sees a false failure. A page refresh will land them logged in.
      console.error('Post-login handling error (login still succeeded):', postErr);
    } finally {
      submittingRef.current = false;
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-100 via-white to-blue-100 flex items-center justify-center px-4">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <img src={LOGO_URL} alt="NLV Logo" className="w-24 h-24 object-contain" />
            </div>
            <h1 className="text-4xl font-bold text-gray-900 mb-2">Welcome Back</h1>
            <p className="text-gray-600">Sign in to your account</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input
                id="email"
                data-testid="login-email-input"
                type="email"
                required
                value={formData.email}
                onChange={(e) => setFormData({...formData, email: e.target.value})}
                className="mt-2"
                placeholder="your@email.com"
              />
            </div>

            <div>
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                data-testid="login-password-input"
                type="password"
                required
                value={formData.password}
                onChange={(e) => setFormData({...formData, password: e.target.value})}
                className="mt-2"
              />
            </div>

            <Button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white font-semibold py-6 text-lg rounded-full"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>
          </form>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate('/forgot-password')}
              className="text-blue-600 text-sm hover:underline mb-4 block"
            >
              Forgot password?
            </button>
            <p className="text-gray-600">
              Don&apos;t have an account?{' '}
              <button
                data-testid="go-to-register-btn"
                onClick={() => navigate('/register')}
                className="text-orange-600 font-semibold hover:underline"
              >
                Join Now
              </button>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
